import { NavLink, Outlet, Route, Routes, useParams } from 'react-router';
import { Carregando } from '../componentes/base';
import { PaginaClientesDoPortal } from './paginas/ClientesPortal';
import { PaginaEntrarNoPortal } from './paginas/EntrarPortal';
import { PaginaFichaDoPortal } from './paginas/FichaPortal';
import { PaginaInicioDoPortal } from './paginas/InicioPortal';
import { PaginaRecebimentosDoPortal } from './paginas/RecebimentosPortal';
import { SessaoPortalProvedor, useSessaoPortal } from './SessaoPortal';

/**
 * Portal das clínicas contratantes.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUE É UM APLICATIVO À PARTE, E NÃO MAIS UMA TELA DO SISTEMA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Quem opera aqui é a recepção da clínica, entre um paciente e outro — não a
 * advogada. Ela precisa de três coisas (cadastrar o cliente, anexar os documentos,
 * ver em que pé está) e não deve nem tropeçar nas outras vinte do escritório.
 *
 * Por isso o portal não tem o trilho lateral, não carrega a sessão da equipe e não
 * compartilha rota com ela. O que ele compartilha são as PEÇAS de interface — mesma
 * tipografia, mesmas cores, mesmos campos —, para a clínica reconhecer que está no
 * sistema do escritório, e não num site qualquer.
 *
 * O TOKEN VIVE NO CAMINHO (`/portal/<token>`). É o link que a clínica guardou nos
 * favoritos: mantê-lo na URL depois do login faz o favorito continuar valendo e faz
 * a volta pedir só a senha. Ele identifica a clínica, não autentica ninguém — quem
 * autentica é o cookie de sessão, emitido pelo servidor contra a senha.
 */
export function PortalApp() {
  return (
    <SessaoPortalProvedor>
      <Routes>
        <Route path=":token" element={<Portao />}>
          <Route index element={<PaginaInicioDoPortal />} />
          <Route path="clientes" element={<PaginaClientesDoPortal />} />
          <Route path="clientes/:id" element={<PaginaFichaDoPortal />} />
          {/* ANTES do curinga, que devolve o início para qualquer caminho
              desconhecido — depois dele, esta rota nunca seria alcançada.
              Sem item no menu de propósito: chega-se aqui pelo indicador. */}
          <Route path="recebimentos" element={<PaginaRecebimentosDoPortal />} />
          <Route path="*" element={<PaginaInicioDoPortal />} />
        </Route>
        <Route path="*" element={<SemLink />} />
      </Routes>
    </SessaoPortalProvedor>
  );
}

/** Só deixa passar quem tem sessão; sem ela, mostra o login com o token do caminho. */
function Portao() {
  const { token = '' } = useParams();
  const { titular, carregando } = useSessaoPortal();

  if (carregando) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-tinta-950">
        <Carregando>
          <span className="text-tinta-500">Abrindo o portal…</span>
        </Carregando>
      </div>
    );
  }

  if (!titular) return <PaginaEntrarNoPortal token={token} />;

  return <Moldura token={token} />;
}

/** Cabeçalho fixo e área de conteúdo. Duas abas — não há uma terceira coisa a fazer. */
function Moldura({ token }: { token: string }) {
  const { titular, clinicas, sair } = useSessaoPortal();
  const base = `/portal/${token}`;

  const aba =
    'rounded-md px-3 py-1.5 text-sm font-medium transition-colors ' +
    'text-[color:var(--texto-suave)] hover:text-[color:var(--texto)]';
  const abaAtiva = 'bg-[color:var(--superficie-afundada)] text-[color:var(--texto)]';

  return (
    <div className="min-h-dvh">
      <header className="border-b bg-[color:var(--superficie-elevada)]">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-6 gap-y-3 px-6 py-3.5">
          {/* A marca é dourada sobre fundo transparente — assenta tanto no papel
              claro quanto na tinta do tema escuro, sem precisar de duas versões. */}
          <img
            src="/marca/marca-horizontal.png"
            alt="Bianca Ávila Advocacia"
            className="h-8 w-auto"
            width={1000}
            height={334}
          />

          <nav className="flex items-center gap-1">
            <NavLink
              to={base}
              end
              className={({ isActive }) => `${aba} ${isActive ? abaAtiva : ''}`}
            >
              Início
            </NavLink>
            <NavLink
              to={`${base}/clientes`}
              className={({ isActive }) => `${aba} ${isActive ? abaAtiva : ''}`}
            >
              Clientes
            </NavLink>
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <span className="hidden text-right text-sm text-[color:var(--texto-suave)] sm:inline">
              {titular}
              {/* Com duas razões sociais, o nome do titular sozinho não diz quais
                  carteiras estão à vista. A linha de baixo diz. */}
              {clinicas.length > 1 && (
                <span className="block text-xs text-[color:var(--texto-tenue)]">
                  {clinicas.map((c) => c.nome).join(' · ')}
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={() => void sair()}
              className="rounded-md border px-3 py-1.5 text-sm text-[color:var(--texto-suave)] transition-colors hover:text-[color:var(--texto)]"
            >
              Sair
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-7">
        <Outlet />
      </main>

      <footer className="mx-auto max-w-6xl px-6 pb-10 text-xs text-[color:var(--texto-tenue)]">
        Cobrança conduzida por Bianca Ávila Advocacia. Tudo o que você faz aqui fica
        registrado na ficha do cliente, com a identificação da clínica.
      </footer>
    </div>
  );
}

/**
 * `/portal` sem token.
 *
 * Acontece quando alguém digita o endereço de cabeça em vez de abrir o link. Não há
 * o que oferecer — o link é a única porta —, então a tela diz isso em vez de mostrar
 * um formulário de login que não teria como funcionar.
 */
function SemLink() {
  return (
    <main className="flex min-h-dvh items-center justify-center bg-tinta-950 px-6 py-12">
      <div className="w-full max-w-[26rem] rounded-lg border border-tinta-800 bg-tinta-900 p-7 text-center">
        <h1 className="text-xl text-papel-100">Portal das clínicas</h1>
        <p className="mt-3 text-sm text-tinta-500">
          O acesso é feito pelo link exclusivo que o escritório enviou à sua clínica.
          Procure-o no e-mail ou no WhatsApp da implantação — ou peça um novo ao
          escritório.
        </p>
      </div>
    </main>
  );
}
